/* eslint-disable react/jsx-max-props-per-line */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { renderStatus } from '../../../../components/v2/cell-renderers/status';
import TableService from '../../../../services/TableService';
import BaseService from '../../../../services/BaseService';
import { statusToChip as integracaoStatusToChip } from '../../Integracoes/constants';
import { formatDigitadoEm } from '../../Nsir/mtbt/utils/integracaoStatus';
import { buildPathWithNsmpId } from '../../Nsir/utils/nsmpUrl';
import {
  getTableV2DetailBodyCellSx,
  getTableV2HeaderCellSx,
  tableV2DetailPanelTitleSx,
  tableV2DetailPanelWrapperSx,
  tableV2EmptyStateSx,
  tableV2PaginationSx,
  tableV2TableSx,
} from '../../../../components/v2/tableV2Styles';
import { isUsoMutuoPosteRow } from '../../Nsir/utils/nsmpRowTypes';
import {
  HISTORICO_NSMP_FILTER_OPTIONS,
  HISTORICO_NSMP_FILTERS,
  isDataNoPeriodo,
} from './constants';
import { metadataChipSx } from './metadataChipSx';

const DETAIL_ROW_HEIGHT = 44;
const DETAIL_PAGE_SIZE = 15;

const formatDate = (value) => formatDigitadoEm(value) ?? '—';

const formatNsmpLabel = (row) => {
  const codigo = row.nsmpCodigo ?? row.codigo ?? row.id;
  const tipo = row.tipoServicoNome ?? row.tipoServico;
  if (codigo && tipo) return `${codigo} · ${tipo}`;
  return codigo ?? tipo ?? '—';
};

const renderIntegracao = (row) => {
  const status = row.integracaoStatus ?? row.integrationStatus;
  if (!status) return '—';
  return renderStatus(integracaoStatusToChip(status));
};

const buildNsmpHref = (nsirId, nsmpId) =>
  buildPathWithNsmpId(`/v2/nsir/${nsirId}`, '', nsmpId);

const resolveNoPeriodo = (row, inicio, fim) => {
  if (row.noPeriodo != null) return Boolean(row.noPeriodo);
  const data = row.dataDigitacao ?? row.digitadoEm;
  return isDataNoPeriodo(inicio, fim, data);
};

const PeriodoChip = ({ noPeriodo }) => (
  <Chip
    label={noPeriodo ? 'No período' : 'Fora do período'}
    size="small"
    variant="outlined"
    color={noPeriodo ? 'default' : 'warning'}
    sx={noPeriodo ? metadataChipSx : { fontWeight: 600 }}
  />
);

PeriodoChip.propTypes = {
  noPeriodo: PropTypes.bool.isRequired,
};

const buildCacheKey = (nsirId, page, filtro, inicio, fim, paginated, lenteId) => {
  const lenteSuffix = lenteId ? `-${lenteId}` : '';
  if (!paginated) {
    return `${nsirId}-client-${filtro}-${inicio ?? ''}-${fim ?? ''}${lenteSuffix}`;
  }
  return `${nsirId}-${page}-${filtro}-${inicio ?? ''}-${fim ?? ''}${lenteSuffix}`;
};

const buildFullListCacheKey = (nsirId, inicio, fim) =>
  `${nsirId}-full-${inicio ?? ''}-${fim ?? ''}`;

const fetchNsmpsPage = async (nsirId, page, filtro, inicio, fim, cacheRef) => {
  const body = {
    inicio,
    fim,
    dateTypes: ['DATA_DIGITACAO'],
    filtro: filtro !== HISTORICO_NSMP_FILTERS.TODOS ? filtro : undefined,
  };

  try {
    const response = await TableService.request(
      `reports/digitacao/me/historico/${nsirId}/nsmps?p=${page}&s=${DETAIL_PAGE_SIZE}`,
      body,
    );
    const data = response?.data;
    if (data?.rows != null && data?.totalRows != null) {
      return {
        rows: data.rows,
        totalRows: data.totalRows,
        paginated: true,
      };
    }
    const list = Array.isArray(data) ? data : data?.itens ?? data?.rows ?? [];
    if (data?.totalRows != null && data?.rows != null) {
      return { rows: data.rows, totalRows: data.totalRows, paginated: true };
    }
    return { rows: list, totalRows: list.length, paginated: false };
  } catch {
    const fullKey = buildFullListCacheKey(nsirId, inicio, fim);
    if (cacheRef?.current?.has(fullKey)) {
      return {
        rows: cacheRef.current.get(fullKey),
        totalRows: cacheRef.current.get(fullKey).length,
        paginated: false,
      };
    }

    const response = await BaseService.get({
      url: `reports/digitacao/me/historico/${nsirId}/nsmps`,
    });
    const data = response?.data;
    const list = Array.isArray(data) ? data : data?.itens ?? data?.rows ?? [];
    cacheRef?.current?.set(fullKey, list);
    return { rows: list, totalRows: list.length, paginated: false };
  }
};

const applyClientFilter = (rows, filtro, inicio, fim, lenteId) => {
  let filtered = rows;
  if (lenteId === 'USO_MUTUO') {
    filtered = filtered.filter((row) => isUsoMutuoPosteRow(row));
  }
  if (filtro === HISTORICO_NSMP_FILTERS.TODOS) return filtered;
  return filtered.filter((row) => {
    const noPeriodo = resolveNoPeriodo(row, inicio, fim);
    const pendente = row.pendente ?? row.pendenteNsmp ?? false;
    if (filtro === HISTORICO_NSMP_FILTERS.PENDENTES) return pendente;
    if (filtro === HISTORICO_NSMP_FILTERS.NO_PERIODO) return noPeriodo;
    if (filtro === HISTORICO_NSMP_FILTERS.FORA_PERIODO) return !noPeriodo;
    return true;
  });
};

const HistoricoNsmpDetail = ({
  nsirId,
  nsirCodigo,
  inicio,
  fim,
  lenteId,
  cacheRef,
  initialFilter,
}) => {
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [filtro, setFiltro] = useState(initialFilter ?? HISTORICO_NSMP_FILTERS.TODOS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [serverPaginated, setServerPaginated] = useState(true);

  useEffect(() => {
    setFiltro(initialFilter ?? HISTORICO_NSMP_FILTERS.TODOS);
    setPage(0);
  }, [initialFilter, nsirId]);

  const loadRows = useCallback(async () => {
    if (!nsirId) {
      setRows([]);
      setTotalRows(0);
      setLoading(false);
      return;
    }

    const serverCacheKey = buildCacheKey(nsirId, page, filtro, inicio, fim, true, lenteId);
    const clientCacheKey = buildCacheKey(nsirId, page, filtro, inicio, fim, false, lenteId);
    const cached = cacheRef?.current?.get(serverCacheKey) ?? cacheRef?.current?.get(clientCacheKey);
    if (cached) {
      setRows(cached.rows);
      setTotalRows(cached.totalRows);
      setServerPaginated(cached.serverPaginated);
      setLoading(false);
      setError(false);
      return;
    }

    setLoading(true);
    setError(false);

    try {
      const result = await fetchNsmpsPage(nsirId, page, filtro, inicio, fim, cacheRef);
      let { rows: nextRows, totalRows: nextTotal, paginated } = result;

      if (lenteId === 'USO_MUTUO') {
        nextRows = nextRows.filter((row) => isUsoMutuoPosteRow(row));
      }

      if (!paginated) {
        const filtered = applyClientFilter(nextRows, filtro, inicio, fim, lenteId);
        nextTotal = filtered.length;
        const start = page * DETAIL_PAGE_SIZE;
        nextRows = filtered.slice(start, start + DETAIL_PAGE_SIZE);
      } else if (filtro !== HISTORICO_NSMP_FILTERS.TODOS) {
        nextRows = applyClientFilter(nextRows, filtro, inicio, fim, lenteId);
        nextTotal = nextRows.length;
      }

      const resolvedCacheKey = buildCacheKey(nsirId, page, filtro, inicio, fim, paginated, lenteId);
      cacheRef?.current?.set(resolvedCacheKey, {
        rows: nextRows,
        totalRows: nextTotal,
        serverPaginated: paginated,
      });

      setRows(nextRows);
      setTotalRows(nextTotal);
      setServerPaginated(paginated);
    } catch {
      setRows([]);
      setTotalRows(0);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [nsirId, page, filtro, inicio, fim, lenteId, cacheRef]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleFilterChange = (_, nextFiltro) => {
    if (!nextFiltro) return;
    setFiltro(nextFiltro);
    setPage(0);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const headerCellSx = getTableV2HeaderCellSx(DETAIL_ROW_HEIGHT);
  const title = nsirCodigo ? `Serviços · NSIR ${nsirCodigo}` : 'Serviços digitados';
  const emptyMessage = filtro === HISTORICO_NSMP_FILTERS.TODOS
    ? 'Nenhum serviço nesta NSIR.'
    : 'Nenhum serviço com este filtro.';

  const filterControls = useMemo(
    () => (
      <ToggleButtonGroup
        size="small"
        value={filtro}
        exclusive
        onChange={handleFilterChange}
        aria-label="Filtrar serviços"
        sx={{ flexWrap: 'wrap' }}
      >
        {HISTORICO_NSMP_FILTER_OPTIONS.map((option) => (
          <ToggleButton key={option.id} value={option.id} sx={{ px: 1.25, py: 0.25 }}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    ),
    [filtro],
  );

  if (loading && !rows.length) {
    return (
      <Box sx={tableV2DetailPanelWrapperSx}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ px: 2, pt: 1 }}
        >
          <Typography component="div" sx={tableV2DetailPanelTitleSx}>
            {title}
          </Typography>
          {filterControls}
        </Stack>
        <LinearProgress sx={{ mx: 2, mb: 1 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={tableV2DetailPanelWrapperSx}>
        <Typography component="div" sx={tableV2DetailPanelTitleSx}>
          {title}
        </Typography>
        <Box sx={{ ...tableV2EmptyStateSx, py: 2, px: 2 }}>
          Não foi possível carregar os serviços desta NSIR.
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={tableV2DetailPanelWrapperSx}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ px: 2, pt: 1, pb: 0.5 }}
      >
        <Typography component="div" sx={{ ...tableV2DetailPanelTitleSx, px: 0, pt: 0 }}>
          {title}
        </Typography>
        {filterControls}
      </Stack>

      {loading && <LinearProgress sx={{ mx: 2, mb: 0.5 }} />}

      {!rows.length ? (
        <Box sx={{ ...tableV2EmptyStateSx, py: 2, px: 2 }}>
          {emptyMessage}
        </Box>
      ) : (
        <>
          <Box sx={{ overflowX: 'auto', px: { xs: 0.5, sm: 1 }, pb: 0.5 }}>
            <Table
              size="medium"
              aria-label="Serviços digitados por NSIR"
              sx={tableV2TableSx}
            >
              <TableHead>
                <TableRow hover={false}>
                  <TableCell sx={headerCellSx}>Serviço</TableCell>
                  <TableCell sx={headerCellSx}>Digitado em</TableCell>
                  <TableCell sx={headerCellSx}>Período</TableCell>
                  <TableCell sx={headerCellSx}>Status Integração</TableCell>
                  <TableCell sx={headerCellSx}>Ação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, rowIndex) => {
                  const nsmpId = row.nsmpId ?? row.id;
                  const cellSx = getTableV2DetailBodyCellSx(DETAIL_ROW_HEIGHT, rowIndex);
                  const isConsulta = row.participacaoMode === 'CONSULTA';
                  const href = buildNsmpHref(nsirId, nsmpId);
                  const absoluteHref = `${window.location.origin}${href}`;
                  const noPeriodo = resolveNoPeriodo(row, inicio, fim);

                  return (
                    <TableRow key={nsmpId ?? rowIndex}>
                      <TableCell sx={cellSx}>{formatNsmpLabel(row)}</TableCell>
                      <TableCell sx={cellSx}>
                        {formatDate(row.dataDigitacao ?? row.digitadoEm)}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <PeriodoChip noPeriodo={noPeriodo} />
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {isConsulta
                          ? renderStatus({ value: 'AGUARDANDO', label: 'consulta' })
                          : renderIntegracao(row)}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Tooltip title="Abrir em nova aba">
                          <IconButton
                            size="small"
                            component="a"
                            href={absoluteHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Abrir em nova aba"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
          {(serverPaginated || totalRows > DETAIL_PAGE_SIZE) && (
            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={DETAIL_PAGE_SIZE}
              rowsPerPageOptions={[DETAIL_PAGE_SIZE]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} de ${count !== -1 ? count : to}`}
              sx={tableV2PaginationSx}
            />
          )}
        </>
      )}
    </Box>
  );
};

HistoricoNsmpDetail.propTypes = {
  nsirId: PropTypes.string,
  nsirCodigo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inicio: PropTypes.number,
  fim: PropTypes.number,
  lenteId: PropTypes.string,
  cacheRef: PropTypes.shape({ current: PropTypes.instanceOf(Map) }),
  initialFilter: PropTypes.string,
};

HistoricoNsmpDetail.defaultProps = {
  nsirId: null,
  nsirCodigo: null,
  inicio: null,
  fim: null,
  lenteId: null,
  cacheRef: null,
  initialFilter: HISTORICO_NSMP_FILTERS.TODOS,
};

export default HistoricoNsmpDetail;
